import type { ServiceRecord } from "../index";

export const service: ServiceRecord = {
  slug: "media-ingest-delivery",
  kind: "domain-vertical",
  title: "Media Ingest & Delivery Systems",
  shortTitle: "Ingest & Delivery",
  status: "draft",
  tier: "experience",
  seoTitle: "Media Ingest & Delivery Systems | General Applications",
  seoDescription:
    "Media ingest and delivery systems for transfer, validation, routing, review handoffs, packaging, and controlled release.",
  summary:
    "Media ingest and delivery systems that make transfer, validation, routing, review, packaging, and release visible instead of leaving critical handoffs to inboxes and folder names.",
  image: "/images/blog/shipping-s1000d-offline-hero.webp",
  relatedCapabilitySlugs: [],
  relatedIndustrySlugs: ["media-entertainment"],
  relatedPostSlugs: ["shipping-s1000d-offline", "multi-terabyte-ingest-choke"],
  engineering: [
    "Transfer, intake, validation, routing, review, packaging, and release workflows",
    "Source, recipient, asset, package, status, version, and evidence relationships",
    "Large-file movement, resumable transfer, verification, and exception design",
    "Delivery controls for permissions, destination, release approval, and recovery",
    "Operational interfaces that show handoff state, blockers, and the next action",
  ],
  body: [
    {
      type: "heading",
      level: 2,
      text: "Transfers Need an Operational Record",
    },
    {
      type: "paragraph",
      text: "Moving a file is easy. Knowing what arrived, whether it is complete, where it belongs, who may receive it, what version it represents, and what failed along the way is the actual delivery system. That information must survive long transfers, partial failures, changed recipients, and the handoff between production and operations.",
    },
    {
      type: "heading",
      level: 2,
      text: "Plan for the Handoff, Not Just the Transfer",
    },
    {
      type: "paragraph",
      text: "Ingest and delivery work crosses people, systems, storage, review, and release authority. A useful workflow makes the state, evidence, exception, and next responsible action explicit so a delayed, rejected, or incomplete package does not become a scavenger hunt.",
    },
  ],
  phases: {
    study: {
      focus:
        "Map incoming and outgoing assets, sources, recipients, validation, review, release authority, failure modes, and operating constraints.",
      produces: [
        "Ingest, transfer, review, packaging, and delivery workflow map",
        "Asset, package, recipient, state, and evidence inventory",
        "Volume, connectivity, timing, security, and recovery constraint analysis",
        "Role, approval, escalation, and exception-responsibility model",
      ],
      fit: {
        good: [
          "Media teams moving valuable or large assets through multiple review, storage, delivery, and recipient handoffs",
          "Organizations replacing untracked transfers, folders, emails, and informal release knowledge with a controlled operational workflow",
        ],
        poor: [
          "A simple file-share link with no validation, review, recipient, release, or recovery requirement",
          "A request to automate delivery while leaving the authoritative version, recipient permission, and release decision undefined",
        ],
      },
    },
    "initial-design": {
      focus:
        "Define the asset, package, state, validation, review, recipient, and release model before choosing transfer or automation technology.",
      produces: [
        "Ingest and delivery state architecture",
        "Asset, package, recipient, permission, and evidence model",
        "Validation, review, approval, and exception workflow design",
        "Transfer, storage, notification, and recovery approach",
      ],
    },
    "detailed-design": {
      focus:
        "Specify transfer behavior, validation, interface, integration, notifications, and exception procedures for the conditions that disrupt ordinary delivery.",
      produces: [
        "Ingest, packaging, transfer, verification, and release specifications",
        "Status, handoff, notification, and escalation interface definitions",
        "Permission, recipient, version, and evidence controls",
        "Retry, recovery, correction, and audit procedures",
      ],
    },
    implementation: {
      focus:
        "Build and validate the workflow with representative assets and real handoffs, including delayed, incomplete, rejected, and corrected deliveries.",
      produces: [
        "Connected ingest, validation, review, packaging, and delivery workflows",
        "Visible asset, package, status, exception, and evidence records",
        "Verified transfer, release, retry, and recovery controls",
        "Operational handoff and support guidance",
      ],
    },
  },
};
